import { StyleSheet, ViewProps } from 'react-native'
import { useRef } from 'react'
import { Draft, ProfileDataPassword } from 'zens-store'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { object, string } from 'yup'

import { Form } from '../form'
import { NavigationConfirm } from '../navigation'
import { AuthPasswordHelp } from '../auth'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
})

interface Props extends ViewProps {
  data: ProfileDataPassword
  onSave?: (draft: Draft<ProfileDataPassword>) => Promise<void>
  onCancel?: (draft: Draft<ProfileDataPassword>) => Promise<void>
  errors?: Record<keyof ProfileDataPassword, string>
}

const ProfilePassword = (props: Props) => {
  const { data, onSave, onCancel, style, ...otherProps } = props
  const { t } = useTranslation()
  const draft = useRef(new Draft<ProfileDataPassword>({ id: data.id })).current

  const config = {
    groups: [
      {
        fields: [
          {
            key: 'password',
            label: t('Current password'),
            fieldProps: {
              autoCapitalize: 'none',
              autoComplete: 'password',
              secureTextEntry: true,
            },
          },
          {
            key: 'new_password',
            label: t('New password'),
            fieldProps: {
              autoCapitalize: 'none',
              autoComplete: 'password',
              secureTextEntry: true,
            },
            help: <AuthPasswordHelp value={draft.data.new_password} />,
          },
        ],
      },
    ],
  }

  const schema = object({
    password: string().required(t('Your current password is required')),
    new_password: string()
      .required(t('A new password is required'))
      .min(6, t('Password must be at least 6 characters'))
      .matches(/[a-z]+/, t('Password must contain at least one lowercase and one uppercase letter'))
      .matches(/[A-Z]+/, t('Password must contain at least one lowercase and one uppercase letter'))
      .matches(/[0-9]+/, t('Password must contain at least one number')),
  })
  return (
    <>
      <Form
        style={[styles.container, style]}
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Save'),
              onPress: onSave,
              validate: true,
            },
            {
              title: t('Cancel'),
              onPress: onCancel,
              light: true,
            },
          ],
        ]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

export default observer(ProfilePassword)
